import { ChangeDetectorRef, Component, HostListener, inject, OnInit } from '@angular/core';
import { NavigationError, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';

import { SidebarComponent } from './shared/components/sidebar/sidebar.component';
import { HeaderComponent } from './shared/components/header/header.component';
import { SwDrawerComponent } from './shared/components/sw-drawer/sw-drawer.component';
import { CameraModelComponent } from './shared/components/camera-model/camera-model.component';
import { ZoneCreateModelComponent } from './shared/components/zone-create-model/zone-create-model.component';
import { SiteModalComponent } from './shared/components/site-modal/site-modal.component';
import { UserManualComponent } from './user-manual/user-manual.component';
import { SocketService } from './services/socket.service';
import { localStorageKeys } from './shared/const/enum';
import { UtilService } from './services/util.service';
import { AuthService } from './services/auth.service';
import { AlertService } from './services/alert.service';
import { GroupService } from './services/group.service';
import { PriorityThemeService } from './services/priority-theme.service';
import { LocationThemeService } from './services/location-theme.service';
import { SiteService } from './services/site.service';

import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { ModalModule } from 'ngx-bootstrap/modal';
import { SocketIoModule } from 'ngx-socket-io';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-root',
  imports: [
    LoadingBarHttpClientModule,
    ToastModule,
    ModalModule,
    SocketIoModule,
    HeaderComponent,
    SidebarComponent,
    SwDrawerComponent,
    CameraModelComponent,
    ZoneCreateModelComponent,
    SiteModalComponent,
    UserManualComponent,
    CommonModule,
    RouterModule
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  @HostListener('window:beforeunload', ['$event'])
  unloadHandler(event: Event): void {
    localStorage.removeItem('chartsData');
  }

  private _utils = inject(UtilService);
  private router = inject(Router);
  private messages = inject(MessageService);
  private socketService = inject(SocketService);
  private dChange = inject(ChangeDetectorRef);
  private authService = inject(AuthService);
  private alertService = inject(AlertService);
  private groupService = inject(GroupService);
  private priorityTheme = inject(PriorityThemeService);
  private locationTheme = inject(LocationThemeService);
  siteService = inject(SiteService);

  title: string = 'SightWatchAdmin';
  sidebar: boolean = true;
  header: boolean = true;
  isSidebarCollaps: boolean = false;
  isMobileSideBarShow: boolean = true;
  isHideLoadingBar: boolean = false;

  /** A chat toast was clicked: open that chat and close the toast. */
  openChatToast(message: { data?: { conversationId?: string } }): void {
    const id = message.data?.conversationId;
    this.messages.clear('swChat');
    if (id) void this.router.navigate(['/chat', id]);
  }

  constructor() {
    // this._userService.getMasterUserDetails();
    this.router.events.subscribe((res) => {
      this.isMobileSideBarShow = false;
      if (res instanceof NavigationError) {
        this.reloadIfStaleChunkError(res.error);
      }
    });
    // Lazy route chunks are content-hashed per build (outputHashing: all). A tab left open
    // across a redeploy still holds the OLD hashes, so clicking any route it hasn't already
    // loaded fails to fetch — the server serves index.html for the missing file instead of a
    // 404, which the browser then rejects as not being a JS module. NavigationError catches
    // this when it surfaces through the router; this is a fallback for anywhere else a lazy
    // import() might reject without going through routing.
    window.addEventListener('unhandledrejection', (event) => {
      this.reloadIfStaleChunkError(event?.reason);
    });
  }

  /**
   * A build-freshness mismatch is fixed by reloading: that fetches the current index.html
   * and current chunk hashes. Guarded by a short cooldown in sessionStorage so a genuinely
   * broken deploy can't reload-loop the tab forever.
   */
  private reloadIfStaleChunkError(error: unknown): void {
    const message = String((error as any)?.message ?? error ?? '');
    const isStaleChunkError = /failed to fetch dynamically imported module|importing a module script failed|chunkloaderror/i.test(message);
    if (!isStaleChunkError) {
      return;
    }
    const reloadedAtKey = 'sw_stale_chunk_reload_at';
    const lastReloadAt = Number(sessionStorage.getItem(reloadedAtKey) || 0);
    if (Date.now() - lastReloadAt < 10000) {
      return;
    }
    sessionStorage.setItem(reloadedAtKey, String(Date.now()));
    window.location.reload();
  }

  ngOnInit() {
    let type = this._utils.getLocalStorage(localStorageKeys.roleType);
    if (type) {
      this._utils.loginUserType = type;
    }
    this._utils.sidebarConfig.subscribe((res) => {
      this.sidebar = res;
    });
    this._utils.headerConfig.subscribe((res) => {
      this.header = res;
    });
    this._utils.hideShowSideBarInMobile.subscribe((res) => {
      this.isMobileSideBarShow = !this.isMobileSideBarShow;
    });
    this._utils.isHideLoadingBar.subscribe((res) => {
      this.isHideLoadingBar = res;
      this.dChange.detectChanges();
    });
    if (type) {
      // Behind the signed-in guard with the rest: the palette endpoint is authorised, and
      // a logged-out visitor has no queue to retint.
      this.priorityTheme.load();
      this.locationTheme.load();
      this.groupService.enableMessageCounters();
      this.getLoginUserDetails();
    }
  }

  getLoginUserDetails() {
    const a: Subscription = this.authService.getLoginUserDetails().subscribe({
      next: (res) => {
        if (res && res.alertsTime) {
          UtilService.minutesDynamic = res.alertsTime;
        }
        this._utils.setLocalStorage(localStorageKeys.userDetails, res);
        // Settings > Alert Escalation Time is global and outranks the per-user alertsTime, so
        // it is fetched right after and lands on the same statics. Fire and forget: every
        // screen that reads them also re-reads on minutesDynamicChange.
        this.loadEscalationMinutes();
        this._utils.minutesDynamicChange.next(true);
        this.socketService.connectSocket();
      }, error: (e) => console.log(e),
      complete: () => a.unsubscribe()
    });
  }

  /** Never throws: without it the app falls back to alertsTime and the built-in defaults. */
  private loadEscalationMinutes(): void {
    const s: Subscription = this.alertService.getAlertEscalationTime().subscribe({
      next: () => this._utils.minutesDynamicChange.next(true),
      error: (e) => console.log(e),
      complete: () => s.unsubscribe()
    });
  }

  showWhiteBanner() {
    const currentUrl = this.router.url;
    const guestGuardRoutes = ['/login', '/forgot-password', '/set-new-password', '/verify-code'];
    const isGuestGuardRoute = guestGuardRoutes.some(route =>
      currentUrl.startsWith(route)
    );

    if (isGuestGuardRoute) {
      return false;
    }
    return true;
  }

  collapsedEvent(ev) {
    this.isSidebarCollaps = ev;
  }

  ngOnDestroy(): void {
    this.socketService.disconnectSocket();
  }

}
